import { useCallback, useEffect, useState } from 'react';
import stakingApi from '@/services/staking';
import BigNumber from 'bignumber.js';
import { Event, Page } from '@/config/track-config';
import { cellDataFormatter, rateText } from '@/utils/util';
import { DEFAULT_VAL } from '@/utils/constants';
import { useStakingStore } from '@/store/staking';
import { Button } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import { WalletButton } from './walletButton';
import { useParams } from '@web3/base-hooks';
import { useWallet } from '@/hooks/wallet';
import { origin } from '@web3/base-utils';
import cls from 'classnames';
import { appPath } from '@web3/base-utils';

import styles from './index.module.less';

interface IProps {
  helpCenterUrl: string;
}

export const Main = ({ helpCenterUrl }: IProps) => {
  const fm = useFm();
  const { thirdChains } = useStakingStore();
  const { openLoginModal, isLogin, isThird, walletAddresses } = useWallet();
  const { locale } = useParams();

  const [data, setData] = useState({
    depositAmountSum: DEFAULT_VAL,
    yield: DEFAULT_VAL,
    rate: '', // 默认不展示rate
  });

  const handleInvestDetail = () => {
    window.top?.open(`${origin}/${locale}${appPath}/myInvest`, '_self');
  };

  const handleTradeHistory = () => {
    if (isLogin) {
      window.top?.open(`${origin}/${locale}${appPath}/history`, '_self');
      return;
    }
    openLoginModal();
  };

  const handleHelpCenter = () => {
    window.top?.open(helpCenterUrl, '_blank');
  };

  const getData = useCallback(() => {
    stakingApi
      .getMyLpTokenSumV3({ walletAddresses })
      .then(({ success, result }: any) => {
        if (success && result) {
          setData({
            depositAmountSum: result.depositAmountSum,
            yield: result?.yesterdayYieldPkg?.yield ?? '0',
            rate: result?.yesterdayYieldPkg?.rate ?? '0',
          });
        }
      });
  }, [isThird, thirdChains, walletAddresses]);

  useEffect(() => {
    if (isLogin) {
      getData();
      return;
    }
    if (!walletAddresses || !walletAddresses?.length) {
      setData({
        depositAmountSum: DEFAULT_VAL,
        yield: DEFAULT_VAL,
        rate: '',
      });
    }
  }, [getData, isLogin, walletAddresses]);

  return (
    <div className={cls('flex justify-between', styles.main)}>
      <div>
        <article className={styles.title}>
          <h1>
            {'WEB3 '}
            <span className={styles.titleRt}>{fm('StakingNewTitle')}</span>
          </h1>
        </article>
        <div className={cls('mt-4 text-xl', styles.desc)}>
          {fm('stakingDesc')}
        </div>
      </div>
      <div className={styles.myInvestCard}>
        <div className="flex justify-between">
          <div>
            <span
              className={cls('flex-row flex-items-center mr-1', styles.label)}
            >
              {fm('myInvestmentTotalValueUsd')}
            </span>
            <div className={cls(styles.value)}>
              {cellDataFormatter(data.depositAmountSum)}{' '}
            </div>
          </div>

          {/* 若未投资，点击投资明细，进入该钱包地址下的投资明细页面 */}
          {isLogin && (
            <Button
              size="x-small"
              variant="secondary"
              className={styles.investBtn}
              onClick={handleInvestDetail}
              data-spm-even={Event.Web3UserClick}
              data-spm-data={JSON.stringify({
                page_name: Page.StakingWebHomepage,
                button_name: 'Earn_Investmentdetail',
              })}
            >
              {fm('investmentDetail')}
            </Button>
          )}
          {/* 否则 进入连接钱包流程 */}
          {!isLogin && <WalletButton />}
        </div>

        <div className="flex-col">
          <span className={styles.label}>{fm('yesterdayEarnings')}</span>
          <span
            className={cls(styles.yieldValue, {
              [styles.green]: BigNumber(data.yield).gt(0),
              [styles.red]: BigNumber(data.yield).lt(0),
            })}
          >
            {cellDataFormatter(data.yield)}

            {!!data.rate && <span>{`(${rateText(data.rate)})`}</span>}
          </span>
        </div>

        <div className={cls('flex gap-4', styles.buttonArea)}>
          <Button
            size="x-small"
            variant="secondary"
            className={styles.button}
            onClick={handleTradeHistory}
            data-spm-even={Event.Web3UserClick}
            data-spm-data={JSON.stringify({
              page_name: Page.StakingWebHomepage,
              button_name: 'Earn_Transactionrecord',
            })}
          >
            <span
              className={cls(
                'transition-all',
                styles.icon,
                styles.tradeHistoryIcon
              )}
            />
            {fm('tradehistoryTms')}
          </Button>
          <Button
            size="x-small"
            variant="secondary"
            onClick={handleHelpCenter}
            className={styles.button}
            data-spm-even={Event.Web3UserClick}
            data-spm-data={JSON.stringify({
              page_name: Page.StakingWebHomepage,
              button_name: 'Earn_Helpcenter',
            })}
          >
            <span
              className={cls('transition-all', styles.icon, styles.helpIcon)}
            />
            {fm('helpCenter')}
          </Button>
        </div>
      </div>
    </div>
  );
};
